import { useMemo } from 'react';
import { useAppSelector } from '../hooks';

export type BuriedDropStatus =
	| 'Delayed'
	| 'Happening soon'
	| 'Planning'
	| 'Action Needed'
	| '';

const BSW_FINAL_REVIEW_STATUS = 'FINALREVIEW';

const BSW_PLANNING_STATUSES: ReadonlySet<string> = new Set([
	'OPEN',
	'PLANNING',
	'SURVEY',
	'PRESURVEY',
	'FINALPLANNING',
	'PERMIT',
	"HELD",
]);

const BSW_CONSTRUCTION_STATUSES: ReadonlySet<string> = new Set(['CONSTRUCTION']);


const BSW_TERMINAL_STATUSES: ReadonlySet<string> = new Set([
	'COMPLETE',
	'COMPLETED',
	'FINALREVIEW',
	'CANCELED',
	'CANCELLED',
]);


const startOfDay = (d: Date): Date =>
	new Date(d.getFullYear(), d.getMonth(), d.getDate());

/** Normalizes a BSW job status string: trims all whitespace and uppercases. */
export const normalizeBswStatus = (status?: string | null): string =>
	String(status ?? '').replace(/\s+/g, '').toUpperCase();

export const isBswJobActive = (bswJobStatus?: string | null): boolean => {
	const normalized = normalizeBswStatus(bswJobStatus);
	return !!normalized && !BSW_TERMINAL_STATUSES.has(normalized);
};



/** Returns true if the BSW job status is complete/completed/canceled/cancelled. */
export const isBswJobCompletedOrCanceled = (
	bswJobStatus?: string | null,
): boolean => {
	const normalized = normalizeBswStatus(bswJobStatus);
	return !!normalized && BSW_TERMINAL_STATUSES.has(normalized);
};

/** Returns true if the order has a buried drop that is complete/completed/canceled/cancelled. */
export const hasCompletedOrCanceledBuriedDrop = (
	burriedDropInfo?: { bswJobStatus?: string | null } | null,
): boolean =>
	!!burriedDropInfo &&
	Object.keys(burriedDropInfo).length > 0 &&
	isBswJobCompletedOrCanceled(burriedDropInfo?.bswJobStatus);


export const isDueWithinTwoDays = (
	dueDate?: string | number | Date | null,
): boolean => {
	if (dueDate === undefined || dueDate === null || dueDate === '') return false;
	const due = new Date(dueDate);
	if (Number.isNaN(due)) return false;

	const diffDays = Math.round(
		(+startOfDay(due) - +startOfDay(new Date())) / (24 * 60 * 60 * 1000)
	);
	return diffDays >= 0 && diffDays <= 2;
};

export const isAppointmentDueDatePast = (
	appointmentDueDate?: string | number | Date | null,
): boolean => {
	if (
		appointmentDueDate === undefined ||
		appointmentDueDate === null ||
		appointmentDueDate === ''
	) return false;

	const appointmentDate = new Date(appointmentDueDate);
	if (Number.isNaN(+appointmentDate)) return false;

	return +startOfDay(appointmentDate) < +startOfDay(new Date());
};

export const getBuriedDropUiStatus = (
	bswJobStatus?: string | null,
	bswDueDate?: string | number | Date | null,
	appointmentDueDate?: string | number | Date | null,
): BuriedDropStatus => {
	const normalized = normalizeBswStatus(bswJobStatus);
	if (!normalized) return '';

	const isFinalReview = normalized === BSW_FINAL_REVIEW_STATUS;
	const isPlanning = BSW_PLANNING_STATUSES.has(normalized);
	const isConstruction = BSW_CONSTRUCTION_STATUSES.has(normalized);

	if (
		isFinalReview &&
		isAppointmentDueDatePast(appointmentDueDate) 
	) {
		return 'Action Needed';
	}

	if ((isPlanning || isConstruction) && isDueWithinTwoDays(appointmentDueDate)) {
		return 'Delayed';
	}
	if (isConstruction) return 'Happening soon';
	if (isPlanning) return 'Planning';
	return '';
};

// Hook to get the UI status of the buried drop based on the BSW job status and due date from the Redux store.
export const useBuriedDropStatus = (): BuriedDropStatus => {
	const bswJobStatus = useAppSelector(
		(state) =>
			(state as any)?.burriedDrop?.burriedDropDetails?.bswJobStatus as
				| string
				| undefined,
	);
	const bswDueDate = useAppSelector(
		(state) =>
			(state as any)?.burriedDrop?.burriedDropDetails?.bswDueDate as
				| string
				| undefined,
	); 

	const appointmentDueDate = useAppSelector(
		(state) =>
			(state as any)?.upComingAppointment?.upcomingAppointment?.upcomingAppointments[0]?.orderDetails
				?.pendingOrderDetails?.[0]?.dueDate as
			| string
			| undefined,
	);

	return useMemo(
		() => getBuriedDropUiStatus(bswJobStatus, bswDueDate, appointmentDueDate),
		[bswJobStatus, bswDueDate, appointmentDueDate],
	);
};
